import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import heroBackground from "@/assets/about-downtown-cinematic.jpg";
import heroVideo from "@/assets/about-downtown-cinematic.mp4.asset.json";
import founderImage from "@/assets/rick-founder-professional.jpg";
import missionBackground from "@/assets/audi-downtown.png";
import whyChooseBackground from "@/assets/oktoberfest-celebration.webp";
import audiLuxury from "@/assets/audi-luxury.jpg";
import { Award, Car, Clock, Shield, MapPin, ArrowRight } from "lucide-react";

const About = () => {
  const schemaMarkup = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "url": "https://ricksblackcarservicesantonio.com",
      "logo": "https://ricksblackcarservicesantonio.com/og-image.jpg",
      "image": "https://ricksblackcarservicesantonio.com/og-image.jpg",
      "description": "Veteran-owned luxury transportation service in San Antonio, TX. Featuring a 2025 Audi Q7 Black Optic SUV with JBSA military base access.",
      "founder": {
        "@type": "Person",
        "name": "Rick Ruiz-Estrada",
        "description": "22-year U.S. Army veteran and founder of Rick's Black Car Service"
      },
      "telephone": "+1-210-296-7409",
      "email": "ricksblackcarservice@outlook.com",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "16034 Rough Oak Street",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "postalCode": "78232",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 29.5850,
        "longitude": -98.4615
      },
      "openingHoursSpecification": [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      }],
      "areaServed": [
        { "@type": "City", "name": "San Antonio" },
        { "@type": "City", "name": "Boerne" },
        { "@type": "City", "name": "New Braunfels" },
        { "@type": "City", "name": "Schertz" },
        { "@type": "City", "name": "Fredericksburg" }
      ],
      "knowsAbout": ["JBSA Transportation", "Military Base Access", "Executive Car Service", "Luxury SUV Transportation"]
    },
    {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      "name": "Rick's Black Car Service About Gallery",
      "description": "Professional photos of Rick's Black Car Service founder, luxury fleet, and service areas in San Antonio",
      "image": [
        {
          "@type": "ImageObject",
          "url": "https://ricksblackcarservicesantonio.com/assets/rick-founder-professional.jpg",
          "caption": "Rick Ruiz-Estrada - Founder and Owner of Rick's Black Car Service, 22-year U.S. Army Veteran",
          "description": "Professional headshot of veteran-owned car service owner in San Antonio providing luxury transportation",
          "width": 800,
          "height": 800,
          "publisher": {
            "@type": "Organization",
            "name": "Rick's Black Car Service"
          }
        },
        {
          "@type": "ImageObject",
          "url": "https://ricksblackcarservicesantonio.com/assets/about-hero-downtown.webp",
          "caption": "San Antonio downtown skyline - Rick's Black Car Service operating area",
          "description": "Downtown San Antonio Texas where Rick's Black Car Service provides premium luxury transportation",
          "width": 1920,
          "height": 1080,
          "publisher": {
            "@type": "Organization",
            "name": "Rick's Black Car Service"
          }
        },
        {
          "@type": "ImageObject",
          "url": "https://ricksblackcarservicesantonio.com/assets/audi-downtown.png",
          "caption": "Rick's Black Car Service 2025 Audi Q7 in downtown San Antonio",
          "description": "Premium luxury SUV providing executive car service in San Antonio with military precision and veteran-owned excellence",
          "width": 1920,
          "height": 1080,
          "publisher": {
            "@type": "Organization",
            "name": "Rick's Black Car Service"
          }
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="About Rick's Black Car Service | Veteran-Owned Luxury Transportation"
        description="Learn about Rick's Black Car Service, a veteran-owned boutique luxury transport provider in San Antonio. 22 years of Army values in every ride."
        canonicalPath="/about"
        schema={schemaMarkup}
      />
      
      <Navbar />
      
      <main className="pt-20 overflow-hidden">
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center animate-fade-in overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={heroVideo.url}
            poster={heroBackground}
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(rgba(10, 15, 26, 0.45), rgba(10, 15, 26, 0.6))" }}
            aria-hidden="true"
          />
          <div className="relative text-center z-10 px-4 max-w-full overflow-hidden">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-primary mb-4 break-words">
              Rick's Black Car Service: Veteran-Owned Luxury Transportation in San Antonio
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-foreground font-light">
              Excellence Forged in Duty. Luxury Delivered with Precision.
            </p>
          </div>
        </section>

        {/* The Story - Introduction with Profile */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="flex flex-col items-center">
              <img 
                src={founderImage} 
                alt="Rick Ruiz-Estrada - 22-Year Army Veteran and Founder of Rick's Black Car Service"
                className="rounded-full w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 lg:w-[450px] lg:h-[450px] object-cover border-[4px] border-foreground/20 shadow-[0_0_40px_rgba(0,0,0,0.3),0_0_80px_rgba(212,175,55,0.1)]"
                loading="eager"
              />
              <div className="mt-6 text-center">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary mb-2">Rick Ruiz-Estrada</h3>
                <p className="text-base sm:text-lg md:text-xl text-foreground/80">Founder & Owner</p>
                <p className="text-sm sm:text-base md:text-lg text-foreground/70 mt-1">U.S. Army Veteran, 22 Years of Service</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-primary flex-shrink-0" />
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary">The Story</h2>
              </div>
              <p className="text-base sm:text-lg leading-relaxed text-foreground/90">
                Founded by <strong className="text-primary">Rick Ruiz-Estrada</strong>, a U.S. Army Veteran with <strong className="text-primary">22 years of service</strong>, 
                Rick's Black Car Service is built on the values of integrity, loyalty, and selfless service. After two decades 
                of military discipline, Rick transitioned his commitment to excellence into providing the most reliable 
                executive car service in San Antonio, TX.
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-foreground/90">
                Every ride reflects the precision and dedication that defined Rick's military career. From punctual pickups 
                to immaculate vehicle presentation, we deliver a professional chauffeur experience that honors the trust 
                you place in us.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2">
                  <Award className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">22-Year Army Veteran</span>
                </div>
                <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">JBSA Base Access</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Fleet & Experience */}
        <section 
          className="py-20 animate-fade-in relative bg-cover bg-center overflow-hidden" 
          style={{ 
            animationDelay: '0.3s',
            backgroundImage: `linear-gradient(rgba(10, 15, 26, 0.6), rgba(10, 15, 26, 0.7)), url(${missionBackground})`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <img 
                  src={audiLuxury} 
                  alt="2025 Audi Q7 Black Optic SUV - Luxury Executive Transportation San Antonio" 
                  className="rounded-lg shadow-2xl w-full"
                />
              </div>
              <div className="order-1 md:order-2 space-y-6">
                <div className="flex items-center gap-3">
                  <Car className="w-8 h-8 text-primary flex-shrink-0" />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary">The Fleet & Experience</h2>
                </div>
                <p className="text-base sm:text-lg leading-relaxed text-foreground/90">
                  When you book with us, you aren't just getting a ride; you are securing a <strong className="text-primary">premium travel experience</strong>. 
                  We operate an immaculate <strong className="text-primary">2025 Audi Q7 Black Optic SUV</strong>, offering high-end features like Valcona 
                  leather seating and a panoramic sunroof—perfect for executive travel, wedding parties, or a night out in 
                  The Pearl or La Cantera.
                </p>
                <ul className="space-y-3">
                  {[
                    'Valcona Leather Interior',
                    'Panoramic Sunroof',
                    'Complimentary Wi-Fi',
                    'Premium Sound System',
                    'Climate-Controlled Cabin'
                  ].map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-foreground/90">
                      <ArrowRight className="h-4 w-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Specialized JBSA & Military Access */}
        <section className="py-20 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Shield className="w-8 h-8 text-primary" />
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary">
                  Specialized JBSA & Military Access
                </h2>
              </div>
              <p className="text-base sm:text-lg text-foreground/80 max-w-3xl mx-auto">
                As a veteran-led business, we maintain official commercial access to Joint Base San Antonio (JBSA), 
                including Lackland AFB, Fort Sam Houston, and Randolph AFB. We specialize in stress-free transportation 
                for military families attending BMT Graduations and personnel traveling to and from BAMC.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'Lackland AFB',
                  description: 'BMT Graduation transportation with direct base access. We understand the importance of this milestone for military families.',
                  link: '/bmt-graduation'
                },
                {
                  title: 'Fort Sam Houston & BAMC',
                  description: 'Reliable transportation for medical appointments at Brooke Army Medical Center and personnel travel.',
                  link: '/bamc-transportation'
                },
                {
                  title: 'Randolph AFB',
                  description: 'Executive officer transport and family services with DBIDS-credentialed chauffeurs.',
                  link: '/randolph-afb-transportation'
                }
              ].map((base) => (
                <Link 
                  key={base.title}
                  to={base.link}
                  className="group bg-card border border-border rounded-lg p-6 hover:border-primary transition-colors"
                >
                  <h3 className="text-xl font-serif font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {base.title}
                  </h3>
                  <p className="text-foreground/80 text-sm leading-relaxed">
                    {base.description}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-primary text-sm font-medium">
                    Learn More <ArrowRight className="h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to="/jbsa-military-transportation">
                <Button size="lg" className="btn-luxury">
                  View All JBSA Services
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section 
          className="py-20 animate-fade-in relative bg-cover bg-center overflow-hidden" 
          style={{ 
            animationDelay: '0.5s',
            backgroundImage: `linear-gradient(rgba(10, 15, 26, 0.6), rgba(10, 15, 26, 0.7)), url(${whyChooseBackground})`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <MapPin className="w-8 h-8 text-primary" />
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary">Service Areas</h2>
              </div>
              <p className="text-base sm:text-lg text-foreground/80 max-w-3xl mx-auto">
                We proudly provide professional chauffeur services to San Antonio's premier neighborhoods and surrounding cities.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-background/80 backdrop-blur-sm border border-border rounded-lg p-6 sm:p-8">
                <h3 className="text-xl font-serif font-bold text-primary mb-4">
                  Premier Neighborhoods
                </h3>
                <ul className="space-y-3">
                  {['Stone Oak', 'The Dominion', 'Alamo Heights', 'Terrell Hills', 'Shavano Park', 'Hill Country Village'].map((area) => (
                    <li key={area} className="flex items-center gap-3 text-foreground/90">
                      <ArrowRight className="h-4 w-4 text-primary flex-shrink-0" />
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-background/80 backdrop-blur-sm border border-border rounded-lg p-6 sm:p-8">
                <h3 className="text-xl font-serif font-bold text-primary mb-4">
                  Surrounding Cities
                </h3>
                <ul className="space-y-3">
                  {[
                    { name: 'Boerne', link: '/boerne-car-service' },
                    { name: 'New Braunfels', link: '/new-braunfels-car-service' },
                    { name: 'Schertz', link: '/san-antonio-car-service' },
                    { name: 'Fredericksburg Wine Country', link: '/wine-tours' },
                    { name: 'Canyon Lake', link: '/canyon-lake-car-service' },
                    { name: 'Fair Oaks Ranch', link: '/fair-oaks-ranch-car-service' }
                  ].map((city) => (
                    <li key={city.name}>
                      <Link 
                        to={city.link}
                        className="flex items-center gap-3 text-foreground/90 hover:text-primary transition-colors"
                      >
                        <ArrowRight className="h-4 w-4 text-primary flex-shrink-0" />
                        {city.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary text-center mb-16">Why Choose Us?</h2>
            
            {/* Feature Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
              <div className="bg-card border border-border p-6 sm:p-8 rounded-lg text-center shadow-lg hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:border-primary">
                <Award className="w-10 h-10 sm:w-14 sm:h-14 text-primary mx-auto mb-4" />
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-primary mb-4">Veteran-Led Excellence</h3>
                <p className="text-sm sm:text-base text-foreground/90">
                  22 years U.S. Army experience brings unmatched discipline and reliability to every ride
                </p>
              </div>
              
              <div className="bg-card border border-border p-6 sm:p-8 rounded-lg text-center shadow-lg hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:border-primary">
                <Car className="w-10 h-10 sm:w-14 sm:h-14 text-primary mx-auto mb-4" />
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-primary mb-4">2025 Audi Q7 Luxury</h3>
                <p className="text-sm sm:text-base text-foreground/90">
                  Exclusive Black Optic SUV offering supreme comfort, safety, and sophistication
                </p>
              </div>
              
              <div className="bg-card border border-border p-6 sm:p-8 rounded-lg text-center shadow-lg hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:border-primary sm:col-span-2 lg:col-span-1">
                <Clock className="w-10 h-10 sm:w-14 sm:h-14 text-primary mx-auto mb-4" />
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-primary mb-4">Punctual & Professional</h3>
                <p className="text-sm sm:text-base text-foreground/90">
                  Military precision ensures on-time arrivals for executives, VIPs, and special events
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 text-center animate-fade-in bg-card/50" style={{ animationDelay: '0.7s' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-primary mb-4">
              Experience the Difference
            </h2>
            <p className="text-base sm:text-lg text-foreground/80 mb-8">
              Book your luxury executive transportation today and discover why San Antonio's most discerning travelers 
              choose Rick's Black Car Service.
            </p>
            <Button 
              asChild
              size="lg"
              className="btn-luxury text-lg sm:text-xl px-8 sm:px-12 py-6 rounded-lg transition-all duration-300 hover:scale-105"
            >
              <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Your Luxury Ride in San Antonio</a>
            </Button>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground">
              Available 24/7 for SAT Airport Transfers, Corporate Events & Special Occasions
            </p>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>
      
      <Footer />
    </div>
  );
};

export default About;
